import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { workItemsApi } from '../services/workItemsApi';
import type { WorkItem } from '../types/workItems';

/**
 * Race-safe single work-item detail fetch (WQ-298), mirroring
 * useCanonicalEventDetail. Refetch on `workItemId` change; only the latest
 * request commits.
 */
export function useWorkItemDetail(workItemId: string | null) {
  const { authFetch } = useAuth();
  const [data, setData] = useState<WorkItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  const fetchNow = useCallback(async () => {
    if (!workItemId) {
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    const myId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const next = await workItemsApi.get(authFetch, workItemId);
      if (myId !== requestIdRef.current) return;
      setData(next);
    } catch (e) {
      if (myId !== requestIdRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
      setData(null);
    } finally {
      if (myId === requestIdRef.current) setIsLoading(false);
    }
  }, [authFetch, workItemId]);

  useEffect(() => {
    fetchNow();
  }, [fetchNow]);

  return { data, isLoading, error, refetch: fetchNow };
}
